import { useQuery } from "@tanstack/react-query";
import { Coin } from "../components/ui/Coin.js";
import { Icon } from "../components/ui/Icon.js";
import { Sparkline } from "../components/ui/Sparkline.js";
import { fetchPortfolio } from "../lib/api.js";

function fmtUSD(n: number, digits = 0): string {
  return n.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/**
 * Mobile read-only portfolio view (≤768px). Order entry is desktop-only by
 * policy: mobile shows holdings, AI insight, P&L stats, and a bottom nav.
 */
export function MobilePortfolioPage() {
  const q = useQuery({
    queryKey: ["portfolio"],
    queryFn: fetchPortfolio,
    refetchInterval: 30_000,
  });

  const holdings = q.data?.holdings ?? [];
  const totalValue = q.data?.totals.value ?? 0;
  const unrealizedPnl = q.data?.totals.unrealizedPnl ?? 0;
  const unrealizedPct = q.data?.totals.unrealizedPnlPct ?? 0;
  const exchanges = new Set(holdings.map((h) => h.exchange)).size;

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        background: "var(--bg)",
        color: "var(--text)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "12px 18px 8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div className="se-logo-mark"><span>S</span></div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>SpotEdge</div>
        </div>
        <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
          <Icon name="bell" size={18} className="muted" />
        </div>
      </div>

      <div
        style={{
          margin: "0 18px 12px",
          padding: "8px 10px",
          background: "rgba(245, 158, 11, 0.06)",
          border: "1px solid rgba(245, 158, 11, 0.2)",
          borderRadius: 6,
          fontSize: 10,
          color: "var(--warn)",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <Icon name="info" size={11} />
        Read-only · trading available on desktop
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "0 18px 16px" }}>
        <div
          className="se-card"
          style={{
            padding: 18,
            marginBottom: 12,
            background: "linear-gradient(180deg, rgba(59, 130, 246, 0.06) 0%, transparent 100%)",
          }}
        >
          <div className="muted" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Total portfolio
          </div>
          <div className="mono tnum" style={{ fontSize: 32, fontWeight: 600, marginTop: 4 }}>
            ${fmtUSD(totalValue)}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
            <span
              className={`mono tnum ${unrealizedPnl >= 0 ? "up" : "down"}`}
              style={{ fontSize: 13, fontWeight: 500 }}
            >
              {unrealizedPnl >= 0 ? "+" : ""}${fmtUSD(Math.abs(unrealizedPnl))} {unrealizedPct >= 0 ? "+" : ""}
              {unrealizedPct.toFixed(2)}%
            </span>
            <span className="muted" style={{ fontSize: 11 }}>unrealized</span>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
          {[
            {
              l: "Holdings",
              v: String(holdings.length),
              sub: `${exchanges} exchange${exchanges === 1 ? "" : "s"}`,
            },
            {
              l: "Cost basis",
              v: `$${fmtUSD(q.data?.totals.cost ?? 0)}`,
              sub: "all-time",
            },
            {
              l: "Largest",
              v: holdings[0]?.asset ?? "—",
              sub: holdings[0]
                ? `${((holdings[0].value / Math.max(totalValue, 1)) * 100).toFixed(1)}%`
                : "—",
            },
            {
              l: "Smallest",
              v: holdings.at(-1)?.asset ?? "—",
              sub: holdings.at(-1)
                ? `${((holdings.at(-1)!.value / Math.max(totalValue, 1)) * 100).toFixed(1)}%`
                : "—",
            },
          ].map((s) => (
            <div key={s.l} className="se-card" style={{ padding: 10 }}>
              <div className="muted" style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {s.l}
              </div>
              <div className="mono tnum" style={{ fontSize: 15, fontWeight: 600, marginTop: 2 }}>
                {s.v}
              </div>
              <div className="muted" style={{ fontSize: 10 }}>{s.sub}</div>
            </div>
          ))}
        </div>

        <div className="se-ai-card" style={{ padding: 12, marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            <Icon name="sparkle" size={12} className="ai-text" />
            <span
              className="ai-text"
              style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}
            >
              AI Insight
            </span>
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.5 }}>
            {holdings.length === 0
              ? "Connect an exchange to get AI-ranked BUY signals on your watchlist."
              : "AI signals refresh every 10 seconds. Open desktop to apply a signal as a paper or live order."}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600 }}>Holdings</div>
          <span className="muted" style={{ fontSize: 11 }}>{holdings.length} assets</span>
        </div>

        <div className="se-card" style={{ padding: 0 }}>
          {holdings.length === 0 ? (
            <div style={{ padding: 16, textAlign: "center" }} className="muted">
              No holdings yet.
            </div>
          ) : (
            holdings.map((h, i) => (
              <div
                key={`${h.exchange}|${h.asset}`}
                style={{
                  padding: "12px 14px",
                  borderBottom:
                    i < holdings.length - 1 ? "1px solid var(--border)" : "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <Coin sym={h.asset} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{h.asset}</span>
                    <span className="mono tnum" style={{ fontSize: 13, fontWeight: 500 }}>
                      ${fmtUSD(h.value)}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                    <span className="muted mono tnum" style={{ fontSize: 10 }}>
                      {h.quantity.toLocaleString("en", { maximumFractionDigits: 4 })} {h.asset}
                    </span>
                    <span
                      className={`mono tnum ${h.unrealizedPnlPct >= 0 ? "up" : "down"}`}
                      style={{ fontSize: 11 }}
                    >
                      {h.unrealizedPnlPct >= 0 ? "+" : ""}
                      {h.unrealizedPnlPct.toFixed(2)}%
                    </span>
                  </div>
                </div>
                <Sparkline
                  data={[h.avgCostBasis, h.lastPrice]}
                  color={h.unrealizedPnlPct >= 0 ? "#22C55E" : "#EF4444"}
                  width={48}
                  height={20}
                />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
